import { createLogger } from '#logger'
import { useMmmPendingStartTracking } from '#mmm/composables/useMmmPendingStartTracking'
import { MMM_DEMO_DASHBOARD_PATH } from '#mmm/utils'
import { resolveMmmProjectRoute } from '#monitor-market-makers/utils/resolveMmmProjectRoute'
import { useQuery } from '@pinia/colada'

const logger = createLogger('check-market-maker-dashboard')

/**
 * Keeps project-scoped MMM setup and dashboard routes aligned with dashboard access.
 * Bare dashboard routes are the public demo and bypass the project access check.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const pendingStartTracking = useMmmPendingStartTracking()
  const urlProjectId = Array.isArray(to.params.projectId)
    ? to.params.projectId[0]
    : to.params.projectId
  const isDashboardRoute = to.path.endsWith(MMM_DEMO_DASHBOARD_PATH)

  if (isDashboardRoute && !urlProjectId) {
    logger.debug('bare demo dashboard, skipping project access check')
    return
  }

  if (!isDashboardRoute && import.meta.client && pendingStartTracking.peek()) {
    logger.debug('pending start-tracking payload present, skipping dashboard redirect')
    return
  }

  const auth = useAuth()
  const projectId = urlProjectId || auth.selectedProjectId

  logger.debug('resolving dashboard project', {
    projectId: auth.project?.id,
    selectedProjectId: auth.selectedProjectId,
    urlProjectId,
  })

  if (!projectId) {
    logger.debug('no project selected, bailing')
    return
  }

  const query = useQuery({
    ...buildApiQuery('/liquidity-monitoring/for-project/mm-project/{projectId}', {
      params: () => ({
        path: {
          projectId: String(projectId),
        },
      }),
    }),
    enabled: () => Boolean(projectId),
  })

  // Calling refresh so we can await the query
  const { data } = await query.refresh()

  logger.debug('dashboard access loaded', {
    isMarketMakerMonitoringEnabled: data?.isMarketMakerMonitoringEnabled,
    isResearch: auth.project?.isResearch,
  })

  const hasDashboardAccess = data?.isMarketMakerMonitoringEnabled
    || (auth.project?.id === projectId && auth.project.isResearch)

  const resolution = resolveMmmProjectRoute({
    projectId,
    isDashboardRoute,
    hasDashboardAccess: Boolean(hasDashboardAccess),
  })

  if (resolution.action === 'redirect') {
    logger.debug('redirecting to matching MMM route', { path: resolution.path })
    return navigateTo(resolution.path)
  }
})
